import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="bg-slate-900 min-h-screen pt-24 pb-16" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="inline-block text-xs tracking-widest text-gray-400 border border-slate-700 rounded-full px-4 py-1.5 mb-4">
            LEGAL
          </span>
          <h1 className="text-4xl sm:text-5xl heading-font text-white mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: 22 April 2026</p>
        </div>

        <div className="prose-legal text-gray-300 space-y-8 leading-relaxed">
          <p>
            This Privacy Policy explains how Audio Apex ("we", "us", "our") collects, uses, and protects your personal information when you use our website (audioapex.co.uk) or hire equipment from us. We are committed to protecting your privacy and handling your data in line with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">1. Who We Are</h2>
            <p className="mb-4">
              Audio Apex is a trading name of GL Music Ltd, an audio equipment hire service operating in London and surrounding areas.
            </p>
            <ul className="space-y-1 text-sm text-gray-400">
              <li><strong className="text-gray-300">Data controller:</strong> GL Music Ltd</li>
              <li><strong className="text-gray-300">Company number:</strong> 14221312</li>
              <li><strong className="text-gray-300">Registered office:</strong> Flat 203, 50 Sulivan Road, London, England, SW6 3DX</li>
              <li><strong className="text-gray-300">Email:</strong> info@audioapex.co.uk</li>
              <li><strong className="text-gray-300">Phone:</strong> 020 8049 5480</li>
            </ul>
            <p className="mt-4 text-sm text-gray-400">
              For any questions about this policy or your data, contact us at info@audioapex.co.uk.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">2. What Information We Collect</h2>
            <p className="mb-3">When you submit an enquiry through our contact form, we collect:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2">
              <li>Your full name</li>
              <li>Your email address</li>
              <li>Your phone number</li>
              <li>Your event details (venue capacity, event date, rental duration)</li>
              <li>Any additional information you include in your message</li>
            </ul>
            <p className="mt-3">
              If you go on to hire equipment from us, we may also collect billing and delivery details necessary to fulfil your booking.
            </p>
            <p className="mt-3">
              We may also collect limited, anonymous technical information when you visit the site (such as pages visited and general location), via our privacy-focused analytics — see our{' '}
              <Link to="/cookies" className="text-white underline underline-offset-2 hover:text-[#e2e8f0] transition-colors">Cookie Notice</Link> for details.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use your personal information to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2">
              <li>Respond to your enquiry and provide a quote</li>
              <li>Arrange, deliver, and manage your equipment hire</li>
              <li>Communicate with you about your booking</li>
              <li>Keep records for accounting, tax, and legal purposes</li>
              <li>Improve our website and services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">4. Our Lawful Basis for Processing</h2>
            <p className="mb-3">We process your data on the following bases:</p>
            <ul className="space-y-2 text-gray-400 ml-2">
              <li><strong className="text-gray-300">Performance of a contract</strong> — to provide the hire services you request</li>
              <li><strong className="text-gray-300">Legitimate interests</strong> — to respond to enquiries and run our business, in a way that does not override your rights</li>
              <li><strong className="text-gray-300">Legal obligation</strong> — to keep records required by law (e.g. for tax)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">5. Who We Share Your Information With</h2>
            <p className="mb-3">We do not sell your data. We may share it only with:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2">
              <li>Service providers who help us operate (e.g. our website host, email provider, and any payment processor) — and only as needed to deliver our services</li>
              <li>Professional advisers (e.g. accountants) where required</li>
              <li>Authorities, where we are legally required to do so</li>
            </ul>
            <p className="mt-3">
              Any provider we use is required to keep your data secure and use it only for the purposes we specify.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">6. How Long We Keep Your Data</h2>
            <p>
              We keep enquiry data for 12 months if it does not lead to a booking. Where you become a customer, we keep records for as long as needed to provide our services and to meet legal and accounting requirements (typically 6 years for financial records).
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">7. Your Rights</h2>
            <p className="mb-3">Under UK GDPR you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data ("right to be forgotten")</li>
              <li>Object to or restrict our processing</li>
              <li>Request your data in a portable format</li>
              <li>Withdraw consent at any time, where processing is based on consent</li>
            </ul>
            <p className="mt-3">
              To exercise any of these, email info@audioapex.co.uk. We will respond within one month.
            </p>
            <p className="mt-3">
              If you are unhappy with how we handle your data, you have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk or by calling 0303 123 1113.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">8. Data Security</h2>
            <p>
              We take reasonable technical and organisational measures to protect your data against unauthorised access, loss, or misuse. However, no online transmission is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">9. Cookies</h2>
            <p>
              Please see our{' '}
              <Link to="/cookies" className="text-white underline underline-offset-2 hover:text-[#e2e8f0] transition-colors">Cookie Notice</Link>{' '}
              for full details on the cookies and analytics we use and how to manage them.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. The latest version will always be available on this page, with the "last updated" date revised accordingly.
            </p>
          </section>

          <section className="border-t border-slate-700 pt-6">
            <h2 className="text-xl heading-font text-white mb-3">11. Contact</h2>
            <p className="text-gray-400 text-sm">
              <strong className="text-gray-300">GL Music Ltd (trading as Audio Apex)</strong><br />
              Email: info@audioapex.co.uk<br />
              Phone: 020 8049 5480<br />
              Registered office: Flat 203, 50 Sulivan Road, London, England, SW6 3DX
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
